/**
 * Iteration 47 — Route-level ErrorBoundary
 *
 * Catches any render/prop-access crash inside child routes and renders a
 * useful fallback instead of a blank white page. Solves the class of bug
 * seen when legacy calibration reports had `section.category === undefined`
 * and downstream `.replace()` threw TypeError.
 */
import React from "react";
import { AlertTriangle, RefreshCw, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, stack: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Best-effort telemetry — never crash on the crash handler itself
    try {
      // eslint-disable-next-line no-console
      console.error("[ErrorBoundary]", error, errorInfo);
      this.setState({ stack: errorInfo?.componentStack });
      // POST to /api/telemetry/client-error if endpoint exists (fire-and-forget)
      const url = `${process.env.REACT_APP_BACKEND_URL || ""}/api/telemetry/client-error`;
      fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("trueline_token") || ""}`,
        },
        body: JSON.stringify({
          message: String(error?.message || error),
          stack: String(error?.stack || "").slice(0, 4000),
          component_stack: String(errorInfo?.componentStack || "").slice(0, 2000),
          path: window.location.pathname,
        }),
      }).catch(() => {});
    } catch { /* swallow */ }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, stack: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const isReport = window.location.pathname.startsWith("/calibration-reports/");
    const rawId = isReport ? window.location.pathname.split("/").pop() : null;

    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6" data-testid="error-boundary">
        <div className="max-w-xl w-full rounded-xl border border-rose-500/30 bg-rose-500/5 p-6 space-y-4">
          <div className="flex items-center gap-2 text-rose-300">
            <AlertTriangle className="w-5 h-5" />
            <h2 className="font-semibold text-lg">This page hit a rendering error</h2>
          </div>
          <p className="text-sm text-slate-300">
            The data for this record is malformed or contains an unexpected shape. Your data is safe — nothing has been changed.
          </p>
          <div className="rounded bg-slate-900/60 border border-slate-800 p-3 text-[11px] font-mono text-slate-400 max-h-40 overflow-auto" data-testid="error-boundary-details">
            <div className="text-rose-300 font-bold mb-1">{String(this.state.error?.message || this.state.error || "Unknown error")}</div>
            {this.state.stack && <pre className="whitespace-pre-wrap">{this.state.stack.slice(0, 800)}</pre>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              onClick={this.handleReset}
              className="bg-rose-600 hover:bg-rose-500"
              data-testid="error-boundary-retry"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Try again
            </Button>
            <Button
              onClick={() => (window.location.href = isReport ? "/calibration-reports" : "/")}
              variant="outline"
              className="border-slate-700"
              data-testid="error-boundary-back"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> Back to list
            </Button>
            {isReport && rawId && (
              <Button
                onClick={async () => {
                  try {
                    const r = await fetch(
                      `${process.env.REACT_APP_BACKEND_URL}/api/calibration-reports/${rawId}/raw`,
                      { headers: { Authorization: `Bearer ${localStorage.getItem("trueline_token")}` } }
                    );
                    const data = await r.json();
                    const w = window.open("", "_blank");
                    w.document.write("<pre>" + JSON.stringify(data, null, 2) + "</pre>");
                  } catch { alert("Could not fetch raw report"); }
                }}
                variant="outline"
                className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                data-testid="error-boundary-raw-json"
              >
                Open Raw JSON
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }
}
